import z from "zod";
import { ConnectorTaskType } from "./connector-task-type.enum.js";
import { ConnectorErrorSchema } from "./connector-error.schema.js";
import { SUPPORTED_CONNECTOR_PROTOCOL_VERSION } from "./protocol.js";
import { GitHubAddCommentInputSchema, GitHubAddCommentOutputSchema, GitHubCreateIssueInputSchema, GitHubCreateIssueOutputSchema, GitHubGetCommentsInputSchema, GitHubGetCommentsOutputSchema, GitHubGetFileContentInputSchema, GitHubGetFileContentOutputSchema, GitHubGetIssueInputSchema, GitHubGetIssueOutputSchema, GitHubGetPullRequestDiffInputSchema, GitHubGetPullRequestDiffOutputSchema, GitHubGetPullRequestInputSchema, GitHubGetPullRequestOutputSchema, GitHubGetRepositoryInputSchema, GitHubGetRepositoryOutputSchema, GitHubGetTreeInputSchema, GitHubGetTreeOutputSchema, GitHubListRepositoriesInputSchema, GitHubListRepositoriesOutputSchema, GitHubSearchInputSchema, GitHubSearchOutputSchema, GitHubTransitionIssueInputSchema, GitHubTransitionIssueOutputSchema, GitHubUpdateIssueInputSchema, GitHubUpdateIssueOutputSchema, } from "./payloads/github.schema.js";
import { ConfluenceCreatePageInputSchema, ConfluenceCreatePageOutputSchema, ConfluenceGetPageInputSchema, ConfluenceGetPageOutputSchema, ConfluenceListSpacesInputSchema, ConfluenceListSpacesOutputSchema, ConfluenceSearchInputSchema, ConfluenceSearchOutputSchema, ConfluenceUpdatePageInputSchema, ConfluenceUpdatePageOutputSchema, } from "./payloads/confluence.schema.js";
import { JiraAddCommentInputSchema, JiraAddCommentOutputSchema, JiraAssignIssueInputSchema, JiraAssignIssueOutputSchema, JiraCreateIssueInputSchema, JiraCreateIssueOutputSchema, JiraCreateSprintInputSchema, JiraCreateSprintOutputSchema, JiraDecomposeEpicInputSchema, JiraDecomposeEpicOutputSchema, JiraGetCommentsInputSchema, JiraGetCommentsOutputSchema, JiraGetIssueInputSchema, JiraGetIssueOutputSchema, JiraLinkIssuesInputSchema, JiraLinkIssuesOutputSchema, JiraListProjectsInputSchema, JiraListProjectsOutputSchema, JiraListProjectMembersInputSchema, JiraListProjectMembersOutputSchema, JiraListSprintsInputSchema, JiraListSprintsOutputSchema, JiraMoveIssuesToSprintInputSchema, JiraMoveIssuesToSprintOutputSchema, JiraSearchInputSchema, JiraSearchOutputSchema, JiraTransitionIssueInputSchema, JiraTransitionIssueOutputSchema, JiraUpdateIssueInputSchema, JiraUpdateIssueOutputSchema, } from "./payloads/jira.schema.js";
const connectorTaskRequestBaseSchema = z.object({
    protocolVersion: z.literal(SUPPORTED_CONNECTOR_PROTOCOL_VERSION),
    taskId: z.string().uuid(),
    idempotencyKey: z.string().min(1),
    workspaceId: z.string().uuid(),
    connectionRef: z.string().min(1),
});
const connectorTaskResultBaseSchema = z.object({
    protocolVersion: z.literal(SUPPORTED_CONNECTOR_PROTOCOL_VERSION),
    taskId: z.string().uuid(),
});
const CONNECTOR_TASK_PAYLOADS = {
    [ConnectorTaskType.GITHUB_LIST_REPOSITORIES]: {
        input: GitHubListRepositoriesInputSchema,
        output: GitHubListRepositoriesOutputSchema,
    },
    [ConnectorTaskType.GITHUB_SEARCH]: {
        input: GitHubSearchInputSchema,
        output: GitHubSearchOutputSchema,
    },
    [ConnectorTaskType.GITHUB_GET_ISSUE]: {
        input: GitHubGetIssueInputSchema,
        output: GitHubGetIssueOutputSchema,
    },
    [ConnectorTaskType.GITHUB_GET_PULL_REQUEST]: {
        input: GitHubGetPullRequestInputSchema,
        output: GitHubGetPullRequestOutputSchema,
    },
    [ConnectorTaskType.GITHUB_GET_REPOSITORY]: {
        input: GitHubGetRepositoryInputSchema,
        output: GitHubGetRepositoryOutputSchema,
    },
    [ConnectorTaskType.GITHUB_CREATE_ISSUE]: {
        input: GitHubCreateIssueInputSchema,
        output: GitHubCreateIssueOutputSchema,
    },
    [ConnectorTaskType.GITHUB_ADD_COMMENT]: {
        input: GitHubAddCommentInputSchema,
        output: GitHubAddCommentOutputSchema,
    },
    [ConnectorTaskType.GITHUB_UPDATE_ISSUE]: {
        input: GitHubUpdateIssueInputSchema,
        output: GitHubUpdateIssueOutputSchema,
    },
    [ConnectorTaskType.GITHUB_TRANSITION_ISSUE]: {
        input: GitHubTransitionIssueInputSchema,
        output: GitHubTransitionIssueOutputSchema,
    },
    [ConnectorTaskType.GITHUB_GET_COMMENTS]: {
        input: GitHubGetCommentsInputSchema,
        output: GitHubGetCommentsOutputSchema,
    },
    [ConnectorTaskType.GITHUB_GET_FILE_CONTENT]: {
        input: GitHubGetFileContentInputSchema,
        output: GitHubGetFileContentOutputSchema,
    },
    [ConnectorTaskType.GITHUB_GET_TREE]: {
        input: GitHubGetTreeInputSchema,
        output: GitHubGetTreeOutputSchema,
    },
    [ConnectorTaskType.GITHUB_GET_PULL_REQUEST_DIFF]: {
        input: GitHubGetPullRequestDiffInputSchema,
        output: GitHubGetPullRequestDiffOutputSchema,
    },
    [ConnectorTaskType.JIRA_LIST_PROJECTS]: {
        input: JiraListProjectsInputSchema,
        output: JiraListProjectsOutputSchema,
    },
    [ConnectorTaskType.JIRA_GET_ISSUE]: {
        input: JiraGetIssueInputSchema,
        output: JiraGetIssueOutputSchema,
    },
    [ConnectorTaskType.JIRA_SEARCH_ISSUES]: {
        input: JiraSearchInputSchema,
        output: JiraSearchOutputSchema,
    },
    [ConnectorTaskType.JIRA_LIST_PROJECT_MEMBERS]: {
        input: JiraListProjectMembersInputSchema,
        output: JiraListProjectMembersOutputSchema,
    },
    [ConnectorTaskType.JIRA_LIST_SPRINTS]: {
        input: JiraListSprintsInputSchema,
        output: JiraListSprintsOutputSchema,
    },
    [ConnectorTaskType.JIRA_ADD_COMMENT]: {
        input: JiraAddCommentInputSchema,
        output: JiraAddCommentOutputSchema,
    },
    [ConnectorTaskType.JIRA_GET_COMMENTS]: {
        input: JiraGetCommentsInputSchema,
        output: JiraGetCommentsOutputSchema,
    },
    [ConnectorTaskType.JIRA_CREATE_ISSUE]: {
        input: JiraCreateIssueInputSchema,
        output: JiraCreateIssueOutputSchema,
    },
    [ConnectorTaskType.JIRA_CREATE_SPRINT]: {
        input: JiraCreateSprintInputSchema,
        output: JiraCreateSprintOutputSchema,
    },
    [ConnectorTaskType.JIRA_MOVE_ISSUES_TO_SPRINT]: {
        input: JiraMoveIssuesToSprintInputSchema,
        output: JiraMoveIssuesToSprintOutputSchema,
    },
    [ConnectorTaskType.JIRA_DECOMPOSE_EPIC]: {
        input: JiraDecomposeEpicInputSchema,
        output: JiraDecomposeEpicOutputSchema,
    },
    [ConnectorTaskType.JIRA_UPDATE_ISSUE]: {
        input: JiraUpdateIssueInputSchema,
        output: JiraUpdateIssueOutputSchema,
    },
    [ConnectorTaskType.JIRA_TRANSITION_ISSUE]: {
        input: JiraTransitionIssueInputSchema,
        output: JiraTransitionIssueOutputSchema,
    },
    [ConnectorTaskType.JIRA_ASSIGN_ISSUE]: {
        input: JiraAssignIssueInputSchema,
        output: JiraAssignIssueOutputSchema,
    },
    [ConnectorTaskType.JIRA_LINK_ISSUES]: {
        input: JiraLinkIssuesInputSchema,
        output: JiraLinkIssuesOutputSchema,
    },
    [ConnectorTaskType.CONFLUENCE_LIST_SPACES]: {
        input: ConfluenceListSpacesInputSchema,
        output: ConfluenceListSpacesOutputSchema,
    },
    [ConnectorTaskType.CONFLUENCE_SEARCH]: {
        input: ConfluenceSearchInputSchema,
        output: ConfluenceSearchOutputSchema,
    },
    [ConnectorTaskType.CONFLUENCE_GET_PAGE]: {
        input: ConfluenceGetPageInputSchema,
        output: ConfluenceGetPageOutputSchema,
    },
    [ConnectorTaskType.CONFLUENCE_CREATE_PAGE]: {
        input: ConfluenceCreatePageInputSchema,
        output: ConfluenceCreatePageOutputSchema,
    },
    [ConnectorTaskType.CONFLUENCE_UPDATE_PAGE]: {
        input: ConfluenceUpdatePageInputSchema,
        output: ConfluenceUpdatePageOutputSchema,
    },
};
function makeConnectorTaskRequestVariant(type, inputSchema) {
    return connectorTaskRequestBaseSchema.extend({
        type: z.literal(type),
        input: inputSchema,
    });
}
function makeConnectorTaskResultVariant(type, outputSchema) {
    return connectorTaskResultBaseSchema
        .extend({
        type: z.literal(type),
        output: outputSchema.optional(),
        error: ConnectorErrorSchema.optional(),
    })
        .superRefine((value, ctx) => {
        const hasOutput = value.output !== undefined;
        const hasError = value.error !== undefined;
        if (hasOutput === hasError) {
            ctx.addIssue({
                code: "custom",
                message: "Connector task result must include exactly one of output or error",
                path: ["output"],
            });
        }
    });
}
const connectorTaskRequestVariants = Object.entries(CONNECTOR_TASK_PAYLOADS).map(([type, { input }]) => makeConnectorTaskRequestVariant(type, input));
const connectorTaskResultVariants = Object.entries(CONNECTOR_TASK_PAYLOADS).map(([type, { output }]) => makeConnectorTaskResultVariant(type, output));
export const ConnectorTaskRequestSchema = z.discriminatedUnion("type", connectorTaskRequestVariants);
export const ConnectorTaskResultSchema = z.discriminatedUnion("type", connectorTaskResultVariants);
export { CONNECTOR_TASK_PAYLOADS };
//# sourceMappingURL=envelope.schema.js.map